import { useState } from "react";
import { useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  Client,
  ClientCompanyInfo,
  ClientIdentity,
  ClientPreferences,
  ClientNote,
  ClientEmail,
  ClientTask,
  Trip
} from "@shared/schema";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  ChevronLeft, 
  User, 
  Building2, 
  CreditCard, 
  Ruler, 
  Plane,
  FileText, 
  Mail, 
  CheckSquare,
  MoreHorizontal,
  Luggage,
  MessageSquare
} from "lucide-react";
import { format } from "date-fns";
import { PersonalInfoPanel } from "@/components/clients/PersonalInfoPanel";
import { RelationshipPanel } from "@/components/clients/RelationshipPanel";
import { IdentityInfoPanel } from "@/components/clients/IdentityInfoPanel";
import { PreferencesPanel } from "@/components/clients/PreferencesPanel";
import { TripsPanel } from "@/components/clients/TripsPanel";
import { NotesPanel } from "@/components/clients/NotesPanel";
import { EmailsPanel } from "@/components/clients/EmailsPanel";
import { TasksPanel } from "@/components/clients/TasksPanel";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";

export function ClientDetail() {
  // Get the client ID from the URL
  const [location] = useLocation();
  const clientId = parseInt(location.split("/").pop() || "0");
  const [activeTab, setActiveTab] = useState("personal");

  // Fetch client data
  const { data: client, isLoading: isLoadingClient, error: clientError } = useQuery<Client>({
    queryKey: [`/api/clients/${clientId}`],
    enabled: !!clientId,
  });

  // Fetch related data based on the active tab
  const { data: companyInfo } = useQuery<ClientCompanyInfo>({
    queryKey: [`/api/clients/${clientId}/company`],
    enabled: !!clientId && activeTab === "relationship",
  });

  const { data: identities = [] } = useQuery<ClientIdentity[]>({
    queryKey: [`/api/clients/${clientId}/identities`],
    enabled: !!clientId && activeTab === "identity",
  });

  const { data: preferences } = useQuery<ClientPreferences>({
    queryKey: [`/api/clients/${clientId}/preferences`],
    enabled: !!clientId && activeTab === "preferences",
  });

  const { data: trips = [] } = useQuery<Trip[]>({
    queryKey: [`/api/clients/${clientId}/trips`],
    enabled: !!clientId && activeTab === "trips",
  });

  const { data: notes = [] } = useQuery<ClientNote[]>({
    queryKey: [`/api/clients/${clientId}/notes`],
    enabled: !!clientId && activeTab === "notes",
  });

  const { data: emails = [] } = useQuery<ClientEmail[]>({
    queryKey: [`/api/clients/${clientId}/emails`],
    enabled: !!clientId && activeTab === "emails",
  });

  const { data: tasks = [] } = useQuery<ClientTask[]>({
    queryKey: [`/api/clients/${clientId}/tasks`],
    enabled: !!clientId && activeTab === "tasks",
  });

  const { data: threads = [] } = useQuery<any[]>({
    queryKey: [`/api/clients/${clientId}/threads`],
    enabled: !!clientId && activeTab === "threads",
  });

  if (isLoadingClient) {
    return (
      <div className="py-8 max-w-[1200px] mx-auto">
        <div className="flex items-center mb-8 gap-4">
          <Skeleton className="h-10 w-10 rounded-full" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-[600px] w-full rounded-lg" />
      </div>
    );
  }

  if (clientError || !client) {
    return (
      <div className="py-8 max-w-[1200px] mx-auto">
        <div className="flex items-center mb-8">
          <Button variant="outline" size="sm" asChild className="mr-4">
            <Link to="/clients-old">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Clients
            </Link>
          </Button>
        </div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          <p>Error loading client. This client may not exist or has been deleted.</p>
          <Button variant="outline" size="sm" asChild className="mt-2">
            <Link to="/clients-old">Return to Client List</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-8 max-w-[1200px] mx-auto">
      {/* Header with back button and actions */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <Button variant="outline" size="sm" asChild className="mr-4">
            <Link to="/clients-old">
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Clients
            </Link>
          </Button>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
            {client.firstName} {client.lastName}
          </h1>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="border-cyan-500 dark:border-cyan-400 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-50 dark:hover:bg-cyan-900/30 hover:text-cyan-700 dark:hover:text-cyan-300"
            asChild
          >
            <Link to={`/clients-old/${clientId}/edit`}>
              Edit Client
            </Link>
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">More actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/trips/new?clientId=${clientId}`}>
                  Create new trip
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem>Export client data</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive dark:text-red-400">
                Delete client
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Tabs for different sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="w-full bg-transparent border-b rounded-none h-auto p-0 justify-start mb-6">
          <TabsTrigger 
            value="personal" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <User className="h-4 w-4 mr-2" />
            Personal Info
          </TabsTrigger>
          <TabsTrigger
            value="relationship" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <Building2 className="h-4 w-4 mr-2" />
            Relationship
          </TabsTrigger>
          <TabsTrigger 
            value="identity" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            Identity
          </TabsTrigger>
          <TabsTrigger 
            value="preferences" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <Ruler className="h-4 w-4 mr-2" />
            Preferences
          </TabsTrigger>
          <TabsTrigger 
            value="trips" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <Luggage className="h-4 w-4 mr-2" />
            Trips
          </TabsTrigger>
          <TabsTrigger 
            value="notes" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <FileText className="h-4 w-4 mr-2" />
            Notes
          </TabsTrigger>
          <TabsTrigger 
            value="emails" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <Mail className="h-4 w-4 mr-2" />
            Emails
          </TabsTrigger>
          <TabsTrigger 
            value="tasks" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <CheckSquare className="h-4 w-4 mr-2" />
            Tasks
          </TabsTrigger>
          <TabsTrigger
            value="threads"
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent py-3 text-sm"
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Threads
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="personal" className="mt-0">
          <PersonalInfoPanel contact={client} />
        </TabsContent>

        <TabsContent value="relationship" className="mt-0">
          <RelationshipPanel
            companyInfo={companyInfo}
            clientId={client.id}
          />
        </TabsContent>

        <TabsContent value="identity" className="mt-0">
          <IdentityInfoPanel
            identities={identities}
            clientId={client.id}
          />
        </TabsContent>

        <TabsContent value="preferences" className="mt-0">
          <PreferencesPanel
            preferences={preferences}
            clientId={client.id}
          />
        </TabsContent>

        <TabsContent value="trips" className="mt-0">
          <TripsPanel
            trips={trips}
            clientId={client.id}
          />
        </TabsContent>

        <TabsContent value="notes" className="mt-0">
          <NotesPanel
            notes={notes}
            clientId={client.id}
          />
        </TabsContent>

        <TabsContent value="emails" className="mt-0">
          <EmailsPanel
            emails={emails}
            clientId={client.id}
          />
        </TabsContent>

        <TabsContent value="tasks" className="mt-0">
          <TasksPanel
            tasks={tasks}
            clientId={client.id}
          />
        </TabsContent>
        <TabsContent value="threads" className="mt-0">
          <div className="space-y-4">
            {threads.length === 0 ? (
              <div className="bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-400">No threads linked to this client yet.</p>
                <p className="text-xs text-gray-500 mt-1">Link threads from the Threads workspace to surface them here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {threads.map((thread: any) => (
                  <div
                    key={thread.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{thread.title}</h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Updated {format(new Date(thread.updatedAt), 'PPpp')} • {thread.provider} / {thread.model}
                        </p>
                      </div>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500">
                        Linked {format(new Date(thread.linkedAt || thread.updatedAt), 'PPpp')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
